import { useSearch } from '@tanstack/react-router';
import { CheckCircle2Icon, CircleAlertIcon } from 'lucide-react';
import { MicrosoftProviderIcon } from '@/components/shared/MicrosoftProviderIcon';
import { Button } from '@/components/ui/NewButton';
import { getMicrosoftFilesSourceLabel, type MicrosoftFilesProvider } from '@/lib/microsoftFiles';
import { useMatterLabels } from '@/lib/useMatterLabels';
import type { AdminConsentSearch } from '@/src/router/search-schemas';

/**
 * Microsoft `/v2.0/adminconsent` redirect target for the SharePoint/OneDrive Entra app.
 *
 * To reach this page, an IT admin clicks an admin-consent link of the form:
 *
 *   https://login.microsoftonline.com/{tenant_id}/v2.0/adminconsent
 *     ?client_id={sharepoint_app_client_id}
 *     &scope=https://graph.microsoft.com/Files.Read.All%20https://graph.microsoft.com/Sites.Read.All%20https://graph.microsoft.com/User.Read
 *     &redirect_uri=https://{app_url}/sharepoint-admin-consent
 *     &state={anything}
 *
 *   https://login.microsoftonline.com/{tenant_id}/v2.0/adminconsent
 *     ?client_id={onedrive_app_client_id}
 *     &scope=https://graph.microsoft.com/Files.Read%20https://graph.microsoft.com/User.Read
 *     &redirect_uri=https://{app_url}/onedrive-admin-consent
 *     &state={anything}
 *
 * On success Microsoft redirects here with `?admin_consent=True&tenant=...&state=...`.
 * On user-cancel or failure, Microsoft redirects here with `?error=...&error_description=...`.
 *
 * The redirect URI must be registered on the OneDrive/SharePoint Entra app in Azure Portal
 * for each environment, otherwise Microsoft returns AADSTS50011.
 */

type ConsentState =
  | { kind: 'success'; tenant: string | null }
  | { kind: 'cancelled' }
  | { kind: 'error'; code: string; description: string | null };

function deriveState(query: AdminConsentSearch): ConsentState {
  const {
    admin_consent: adminConsent,
    error,
    error_description: errorDescription,
    tenant,
  } = query;

  if (adminConsent === 'True') {
    return { kind: 'success', tenant: tenant ?? null };
  }
  if (!error || error === 'access_denied') {
    return { kind: 'cancelled' };
  }
  return { kind: 'error', code: error, description: errorDescription ?? null };
}

type AdminConsentRoutePath = '/onedrive-admin-consent' | '/sharepoint-admin-consent';

interface MicrosoftAdminConsentResultProps {
  provider: MicrosoftFilesProvider;
  from: AdminConsentRoutePath;
}

export function MicrosoftAdminConsentResult({ provider, from }: MicrosoftAdminConsentResultProps) {
  const search = useSearch({ from });
  const providerLabel = getMicrosoftFilesSourceLabel(provider);
  const state = deriveState(search);
  const label = useMatterLabels();

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <title>{`Crimson ${providerLabel} admin consent`}</title>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.08)] p-8">
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center gap-3">
            <img
              src="/logo-icon.svg"
              alt="Crimson"
              width={36}
              height={36}
              className="h-9 w-auto"
            />
            <span className="text-gray-300">+</span>
            <MicrosoftProviderIcon provider={provider} className="h-9 w-9" />
          </div>

          {state.kind === 'success' && (
            <>
              <div className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2Icon className="h-6 w-6 text-green-600" aria-hidden="true" />
              </div>
              <h1 className="mt-4 text-xl font-semibold text-gray-900 tracking-tight">
                Crimson is approved for {providerLabel}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Anyone in your tenant can now connect {providerLabel} inside Crimson without being
                asked to approve permissions individually.
              </p>
              {state.tenant && (
                <p className="mt-3 text-xs text-gray-400">
                  Tenant: <span className="font-mono">{state.tenant}</span>
                </p>
              )}
              <Button
                href="/auth/login"
                variant="crimson"
                size="md"
                text="Sign in to Crimson"
                className="mt-6 w-full"
              />
            </>
          )}

          {state.kind === 'cancelled' && (
            <>
              <div className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <CircleAlertIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />
              </div>
              <h1 className="mt-4 text-xl font-semibold text-gray-900 tracking-tight">
                {providerLabel} consent was not granted
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {label('admin-consent-cancelled-body', { provider: providerLabel })}
              </p>
              <p className="mt-4 text-xs text-gray-500">
                Need a fresh link? Reach out to{' '}
                <a
                  href="mailto:support@crimson.law"
                  className="text-crimson-600 hover:underline"
                >
                  support@crimson.law
                </a>
                .
              </p>
            </>
          )}

          {state.kind === 'error' && (
            <>
              <div className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <CircleAlertIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
              <h1 className="mt-4 text-xl font-semibold text-gray-900 tracking-tight">
                Microsoft could not complete the {providerLabel} consent
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {state.description ??
                  'No further detail was provided by Microsoft. Please try the original link again.'}
              </p>
              <p className="mt-3 text-xs text-gray-400">
                Error code: <span className="font-mono">{state.code}</span>
              </p>
              <p className="mt-4 text-xs text-gray-500">
                If this keeps happening, contact{' '}
                <a
                  href="mailto:support@crimson.law"
                  className="text-crimson-600 hover:underline"
                >
                  support@crimson.law
                </a>{' '}
                with the error code above.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
